// src/hooks/useThemeManager.js
import { useState, useEffect } from 'react';
import { DEFAULT_THEMES } from '../constants/themePresets';

export const useThemeManager = (eventId, initialTheme = null) => {
  const [theme, setTheme] = useState(initialTheme || DEFAULT_THEMES.modern);
  const [isEditing, setIsEditing] = useState(false);

  // Load theme from localStorage or API
  useEffect(() => {
    if (eventId) {
      const savedTheme = localStorage.getItem(`event-theme-${eventId}`);
      if (savedTheme) {
        setTheme(JSON.parse(savedTheme));
      }
    }
  }, [eventId]);

  // Save theme to localStorage
  const saveTheme = (newTheme) => {
    setTheme(newTheme);
    if (eventId) {
      localStorage.setItem(`event-theme-${eventId}`, JSON.stringify(newTheme));
    }
  };

  // Apply a preset theme
  const applyPreset = (presetName) => {
    if (DEFAULT_THEMES[presetName]) {
      saveTheme(DEFAULT_THEMES[presetName]);
    }
  };

  // Update specific color
  const updateColor = (colorKey, value) => {
    saveTheme({
      ...theme,
      colors: {
        ...theme.colors,
        [colorKey]: value
      }
    });
  };

  // Update typography
  const updateTypography = (fontKey, value) => {
    saveTheme({
      ...theme,
      typography: {
        ...theme.typography,
        [fontKey]: value
      }
    });
  };

  // Reset to default
  const resetTheme = () => {
    saveTheme(DEFAULT_THEMES.modern);
  };

  // Export theme as CSS variables string
  const exportThemeAsCSS = () => {
    return `
:root {
  --color-primary: ${theme.colors.primary};
  --color-secondary: ${theme.colors.secondary};
  --color-accent: ${theme.colors.accent};
  --color-background: ${theme.colors.background};
  --color-text: ${theme.colors.text};
  --color-text-light: ${theme.colors.textLight};
  --font-primary: ${theme.typography.primary};
  --font-secondary: ${theme.typography.secondary};
  --font-size-base: ${theme.typography.baseSize};
}`;
  };

  return {
    theme,
    setTheme: saveTheme,
    isEditing,
    setIsEditing,
    applyPreset,
    updateColor,
    updateTypography,
    resetTheme,
    exportThemeAsCSS,
    availablePresets: DEFAULT_THEMES
  };
};